import { AlertTriangle, CheckCircle2, Circle, ShieldAlert } from 'lucide-react';

import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';

export interface ExecutionReconciliationView {
  tranches: Array<{
    label: 'TP1' | 'TP2' | 'TP3';
    targetPrice: number;
    plannedQty: number;
    executedQty: number;
    status: 'PENDING' | 'PARTIAL' | 'EXECUTED';
    executedAt: string | null;
  }>;
  runnerPlannedQty: number;
  runnerExitedQty: number;
  offPlanExitQty: number;
  nextStage: 'TP1' | 'TP2' | 'TP3' | 'RUNNER';
  linkedExitFillCount: number;
}

export interface ExecutionDisciplineView {
  contractBinding?: {
    planId: string;
    unlinkedExitExecutions: number;
    unlinkedExitQuantity: number;
    unlinkedExitFills: Array<{
      id: string;
      quantity: number;
      price: number | null;
      occurredAt: string | null;
    }>;
  } | null;
  executionReconciliation?: ExecutionReconciliationView | null;
  nextRequiredAction: {
    code: string;
    label: string;
    detail: string;
  };
}

function statusTone(status: 'PENDING' | 'PARTIAL' | 'EXECUTED') {
  if (status === 'EXECUTED') return 'success' as const;
  if (status === 'PARTIAL') return 'warning' as const;
  return 'neutral' as const;
}

export function ExecutionDisciplineCard({
  discipline,
  linkingTradeId,
  onLinkExitFill,
}: {
  discipline: ExecutionDisciplineView;
  linkingTradeId?: string | null;
  onLinkExitFill?: (input: { planId: string; tradeId: string }) => void;
}) {
  const reconciliation = discipline.executionReconciliation;
  const binding = discipline.contractBinding;
  const needsReconciliation =
    discipline.nextRequiredAction.code === 'RECONCILE_EXECUTION' ||
    (reconciliation?.offPlanExitQty ?? 0) > 0 ||
    (binding?.unlinkedExitExecutions ?? 0) > 0;

  return (
    <Card
      header={
        <div>
          <h2 className="text-lg font-semibold">Execution discipline</h2>
          <p className="text-sm text-text-muted">
            Actual exits are reconciled against the frozen swing contract, not inferred from
            position size.
          </p>
        </div>
      }
    >
      <div
        className={`rounded border p-4 ${
          needsReconciliation ? 'border-danger/60 bg-danger/10' : 'border-primary/50 bg-primary/5'
        }`}
      >
        <div className="flex items-start gap-3">
          {needsReconciliation ? (
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
          ) : (
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden="true" />
          )}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              Next required action
            </div>
            <div className="mt-1 text-lg font-semibold">{discipline.nextRequiredAction.label}</div>
            <p className="mt-1 text-sm text-text-muted">{discipline.nextRequiredAction.detail}</p>
          </div>
        </div>
      </div>

      {reconciliation ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-4">
          {reconciliation.tranches.map((tranche) => (
            <div key={tranche.label} className="rounded border border-border bg-bg-muted p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{tranche.label}</span>
                <Badge tone={statusTone(tranche.status)}>{tranche.status}</Badge>
              </div>
              <div className="mt-2 text-sm tabular-nums text-text-muted">
                {tranche.executedQty} / {tranche.plannedQty} shares
              </div>
              <div className="mt-1 text-sm tabular-nums">Target {tranche.targetPrice}</div>
            </div>
          ))}
          <div className="rounded border border-border bg-bg-muted p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold">RUNNER</span>
              {reconciliation.runnerExitedQty > 0 ? (
                <AlertTriangle className="h-4 w-4 text-warn" aria-label="Runner reduced" />
              ) : (
                <Circle className="h-4 w-4 text-text-muted" aria-hidden="true" />
              )}
            </div>
            <div className="mt-2 text-sm tabular-nums text-text-muted">
              Planned {reconciliation.runnerPlannedQty} shares
            </div>
            <div className="mt-1 text-sm tabular-nums">{reconciliation.runnerExitedQty} exited</div>
          </div>
        </div>
      ) : (
        <p className="mt-4 text-sm text-text-muted">
          No linked profit-taking fills have been reconciled for this position yet.
        </p>
      )}

      {needsReconciliation ? (
        <div className="mt-4 rounded border border-warn/60 bg-warn/10 p-3 text-sm">
          <div className="font-semibold">Execution exception</div>
          {binding && binding.unlinkedExitExecutions > 0 ? (
            <>
              <p className="mt-1 text-text-muted">
                {binding.unlinkedExitExecutions} unlinked exit fill(s),{' '}
                {binding.unlinkedExitQuantity} shares total. Link each genuine exit to this frozen
                plan; its price will determine whether it satisfies a target or remains OFF PLAN.
              </p>
              {binding.unlinkedExitFills.length > 0 ? (
                <div className="mt-3 space-y-2">
                  {binding.unlinkedExitFills.map((fill) => (
                    <div
                      key={fill.id}
                      className="flex flex-col gap-2 rounded border border-border bg-surface p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="tabular-nums">
                        <div className="font-medium">
                          {fill.quantity} shares{fill.price === null ? '' : ` @ ${fill.price}`}
                        </div>
                        <div className="text-xs text-text-muted">
                          {fill.occurredAt
                            ? fill.occurredAt.slice(0, 10)
                            : 'Execution date unavailable'}
                        </div>
                      </div>
                      {onLinkExitFill ? (
                        <Button
                          size="sm"
                          disabled={Boolean(linkingTradeId)}
                          onClick={() =>
                            onLinkExitFill({ planId: binding.planId, tradeId: fill.id })
                          }
                        >
                          {linkingTradeId === fill.id ? 'Linking…' : 'Link to frozen plan'}
                        </Button>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
          {(reconciliation?.offPlanExitQty ?? 0) > 0 ? (
            <p className="mt-2 text-text-muted">
              {reconciliation?.offPlanExitQty} shares were exited below the required target and are
              classified OFF PLAN. Linking a fill never rewrites the original targets or risk
              baseline.
            </p>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
